// Tách khỏi frameCache.ts để worker giải mã import được mà không kéo theo pool tạo chính worker đó.

export type DecodedFrame = {
  width: number;
  height: number;
  // RGB, 8-bit per channel, row-major. Packed in the decoder (worker for JPEG)
  // so the main thread can publish frames with a single buffer copy.
  rgb: Uint8Array;
};

export type NativeFrameLayout = {
  width: number;
  height: number;
  samplesPerPixel: number;
  photometric: string;
  // DICOM PlanarConfiguration (0028,0006): 0 = pixel-interleaved (RGBRGB...),
  // 1 = plane-interleaved (RRR...GGG...BBB...).
  planarConfiguration: number;
};

// Convert a raw uncompressed frame to packed RGB. Supports the two photometric
// interpretations our sample corpus shows in the native PixelData path:
// - 'RGB' with samplesPerPixel=3 (interleaved RGB)
// - 'MONOCHROME2' with samplesPerPixel=1 (grayscale, replicated to RGB)
export function decodeNativeFrame(
  bytes: Uint8Array,
  layout: NativeFrameLayout
): DecodedFrame {
  const { width, height, samplesPerPixel, photometric, planarConfiguration } =
    layout;
  const pixelCount = width * height;
  const out = new Uint8Array(pixelCount * 3);

  if (samplesPerPixel === 1) {
    // Grayscale — replicate luminance to R, G, B.
    for (let i = 0; i < pixelCount; i++) {
      const v = bytes[i] ?? 0;
      const j = i * 3;
      out[j] = v;
      out[j + 1] = v;
      out[j + 2] = v;
    }
  } else if (
    samplesPerPixel === 3 &&
    (photometric === 'RGB' || photometric === 'PALETTE COLOR')
  ) {
    if (planarConfiguration === 1) {
      // RRRR...GGGG...BBBB...
      const plane = pixelCount;
      for (let i = 0; i < pixelCount; i++) {
        const j = i * 3;
        out[j] = bytes[i] ?? 0;
        out[j + 1] = bytes[plane + i] ?? 0;
        out[j + 2] = bytes[2 * plane + i] ?? 0;
      }
    } else {
      for (let i = 0; i < pixelCount; i++) {
        const k = i * 3;
        out[k] = bytes[k] ?? 0;
        out[k + 1] = bytes[k + 1] ?? 0;
        out[k + 2] = bytes[k + 2] ?? 0;
      }
    }
  } else {
    throw new Error(
      `Unsupported native frame format: ${photometric} samplesPerPixel=${samplesPerPixel}`
    );
  }

  return { width, height, rgb: out };
}
